import type { ISODateTime } from './inventory';

/**
 * Domain model for the Cargonizer-backed pallet tracking proposal (remora
 * SPEC.md §9a/§9b, tidaltech/koi#7882), backed by leatherback's
 * ShipmentsService — see api/shipments-api.ts. Not yet an approved
 * SPEC.md feature.
 */

// Matches the wire format: leatherback's SerialAssignment.Source enum
// serializes as its uppercase name (protojson default), same convention
// domain/inventory.ts's ItemStatus already follows.
export type SerialSource = 'HEARTBEAT_INFERENCE' | 'FIELD_ENTRY';

export interface SerialAssignment {
  itemId: string;
  source: SerialSource;
  // Set only when source === 'HEARTBEAT_INFERENCE': this is a candidate,
  // not a confirmed pallet manifest entry, until a person accepts it.
  confidence?: 'UNCONFIRMED' | undefined;
  note?: string | undefined;
}

// Fixed vocabulary per §9b — free-text units would stay unqueryable.
// Matches leatherback's ConsumableLine.Unit enum names.
export type ConsumableUnit = 'METERS' | 'PIECES' | 'KILOGRAMS';

export interface ConsumableLine {
  description: string;
  quantity: number;
  unit: ConsumableUnit;
  // Only settable once/if leatherback's parts.type catalog gains a
  // consumable category (§5 old item 5) — deliberately optional today.
  partId?: string | undefined;
}

// Structured rather than one free-text string because Cargonizer's own
// consignee address needs the components separately.
export interface ShipmentAddress {
  line1: string;
  line2?: string | undefined;
  postcode: string;
  city: string;
}

export interface ShipmentPallet {
  id: string;
  // Cargonizer pieces[].number for this physical pallet — printed on its
  // label. One tracking number per pallet, always assigned by Cargonizer.
  cargonizerPieceNumber: string;
  weightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  serials: SerialAssignment[];
  consumables: ConsumableLine[];
}

// Sourced from the Bring Developer Tracking API — Bring-only today (§9a).
// Absent whenever the carrier isn't Bring, or Bring hasn't reported
// delivery yet; never guessed or defaulted.
export interface DeliveryStatus {
  status: string;
  deliveredAt: ISODateTime;
  source: 'BRING_TRACKING_API';
}

// OUTBOUND is Tidal to a customer or vendor; RETURN is a customer Site back
// to a Tidal location (tidaltech/koi#8085). Shipments created before
// directions existed are outbound.
export type ShipmentDirection = 'OUTBOUND' | 'RETURN';

// One end of a shipment: a named address, linked to a Site when it is one.
// The address is a snapshot taken at booking time.
export interface ShipmentEndpoint {
  customerId?: string | undefined;
  siteId?: string | undefined;
  name: string;
  contactName?: string | undefined;
  contactPhone?: string | undefined;
  address?: ShipmentAddress | undefined;
}

export const shipmentStates = ['PENDING', 'TRANSFERRED', 'CANCELLED'] as const;
/** CANCELLED: booked but never sent; it isn't tracked and its units aren't touched. */
export type ShipmentState = (typeof shipmentStates)[number];

export const shipmentStateLabels: Record<ShipmentState, string> = {
  PENDING: 'Pending',
  TRANSFERRED: 'Transferred to carrier',
  CANCELLED: 'Cancelled',
};

/** Whether a shipment can still be cancelled: not handed to the carrier yet. */
export function canCancelShipment(shipment: Pick<Shipment, 'state' | 'delivery'>): boolean {
  return shipment.state === 'PENDING' && !shipment.delivery;
}

export interface Shipment {
  id: string;
  direction: ShipmentDirection;
  // Where a return is collected. Outbound shipments leave from Tidal and
  // leave it unset.
  origin?: ShipmentEndpoint | undefined;
  destinationSiteId?: string | undefined;
  destinationCustomerId?: string | undefined;
  destinationContactName?: string | undefined;
  // The recipient's phone sent to Cargonizer (outbound); a return's pickup
  // phone is on origin.
  destinationContactPhone?: string | undefined;
  // Set when koi booked the shipment in Cargonizer; the label can then be
  // fetched with getShipmentLabel.
  cargonizerConsignmentId?: string | undefined;
  cargonizerConsignmentNumber: string;
  carrier: string;
  destinationCustomer: string;
  destinationAddress: ShipmentAddress;
  shippedAt: ISODateTime;
  state: ShipmentState;
  // Why it was cancelled, if the person said; only when CANCELLED.
  cancelNote?: string | undefined;
  // Cargonizer's own tracking-url, carrier-dependent — populated for Bring
  // (sporing.bring.no) but confirmed empty for Nordvest Transport, the
  // carrier on the seeded example below. Absent, not defaulted to a guess.
  trackingUrl?: string | undefined;
  delivery?: DeliveryStatus | undefined;
  pallets: ShipmentPallet[];
  // §9a addition, 2026-08-24, per Sivert's review question: serials known
  // to be on this shipment with no specific pallet recorded — not every
  // shipment is on pallets (Bring often ships single boxes), and even a
  // multi-pallet one shouldn't force a per-pallet guess when that split
  // isn't actually known.
  unassignedSerials: SerialAssignment[];
  // Free-text, e.g. "replacement camera" -- why the shipment went out.
  reason?: string | undefined;
  // Cargonizer's sender's reference ("Avsenders referanse"): what X-Ledger
  // needs to rebill the freight to the customer (tidaltech/koi#8233).
  senderReference?: string | undefined;
  version: number;
  lastModifiedBy?: string | undefined;
  lastModified?: ISODateTime | undefined;
  // Who originally asked for this shipment (Google auth email) -- set once
  // at creation server-side, unlike lastModifiedBy which reflects whoever
  // most recently edited it (e.g. scanned serials on afterward).
  requestedBy?: string | undefined;
}

/** A listed shipment that could not be read; the rest of the list still shows. */
export interface SkippedShipment {
  id: string;
  problem: string;
}

export interface ShipmentList {
  shipments: Shipment[];
  skipped: SkippedShipment[];
}

// leatherback's UpdateShipment always replaces `pallets` and
// `unassignedSerials` wholesale rather than merging element-by-element, so
// any patch that touches either one must include the full desired array —
// same convention ItemPatch/version already established. requestedBy is
// server-set at creation and never patchable, same reasoning as id/version.
export type ShipmentPatch = Partial<
  Omit<
    Shipment,
    | 'id'
    | 'version'
    | 'lastModified'
    | 'lastModifiedBy'
    | 'requestedBy'
    | 'direction'
    | 'origin'
    | 'destinationSiteId'
    | 'destinationCustomerId'
    | 'cargonizerConsignmentId'
  >
> & {
  id: string;
  version: number;
};

// Manual creation: the shipment already exists in Cargonizer (created via
// its own web UI), and this just records the resulting numbers — pallets
// start with no serials/consumables, those are added afterward via
// updateShipment.
export type ShipmentCreateInput = Pick<
  Shipment,
  | 'cargonizerConsignmentNumber'
  | 'carrier'
  | 'destinationCustomer'
  | 'destinationAddress'
  | 'state'
  | 'reason'
> & {
  pallets: Omit<ShipmentPallet, 'serials' | 'consumables'>[];
};

// Carriers the Cargonizer integration actually supports today -- see
// lib/go/cargonizer's carrier map in leatherback. Free text can't
// reliably map to a Cargonizer transport_agreement, so callers must pick
// from this fixed list rather than typing the carrier name.
export const CARGONIZER_CARRIERS = [
  'Nordvest Transport',
  'Bring — Pakke til bedrift',
  'Bring — Pakke til bedrift Ekspress',
] as const;

// Creation via leatherback's CreateShipmentViaCargonizer: no piece numbers
// or consignment number -- Cargonizer assigns those, and they come back on
// the created Shipment.
export type ShipmentCreateViaCargonizerInput = {
  carrier: (typeof CARGONIZER_CARRIERS)[number];
  destinationCustomer: string;
  destinationAddress: ShipmentAddress;
  // Set when the destination is a Site; must not be a Tidal location.
  destinationSiteId?: string | undefined;
  // Sent to Cargonizer so the carrier can reach the recipient; empty ones
  // fall back to the destination Site's contact.
  destinationContactName?: string | undefined;
  destinationContactPhone?: string | undefined;
  pallets: Pick<ShipmentPallet, 'weightKg' | 'lengthCm' | 'widthCm' | 'heightCm'>[];
  reason?: string | undefined;
  // Required: Cargonizer's sender's reference ("Avsenders referanse"), the
  // project number X-Ledger rebills the freight to, or "Intern frakt"
  // (tidaltech/koi#8233).
  senderReference: string;
};

// How customer returns can be booked -- see lib/go/cargonizer's return
// agreements in leatherback. Nordvest has no return product: a Nordvest
// return is an ordinary consignment to TidalX, collected at the customer
// site, as the warehouse books them. Most returns go with Nordvest.
export const CARGONIZER_RETURN_CARRIERS = [
  'Nordvest Transport',
  'Bring — Retur pakke fra bedrift',
  'Bring — Retur ekspress',
] as const;

/**
 * Bring's products here are all parcels (boxes up to 30 kg); Nordvest takes
 * pallets. Matches cargonizer.ShipsBoxes in Leatherback (Sivert, 2026-10-16).
 */
export function shipsBoxes(carrier: string): boolean {
  return carrier.startsWith('Bring');
}

/**
 * Whether a driver collects the return at the customer's pickup address
 * (Nordvest), so the pickup needs a contact person and phone for the driver
 * to call. Bring's return products are dropped off by the customer.
 * Matches lib/go/cargonizer's CollectsReturn.
 */
export function returnIsCollected(carrier: string): boolean {
  return carrier === 'Nordvest Transport';
}

// Who customer returns go to, as the customer instructions always say
// (the warehouse, 2026-09-29): the consignee leatherback puts on a
// Nordvest return (its shipment_return_consignee_* settings), whatever the
// delivery address.
export const RETURN_CONSIGNEE = 'Tidalx Norway AS, Maskinveien 11, 6517 Kristiansund N';

/**
 * The first package missing a measurement, numbered from 1, or null.
 * Carriers charge extra for missing or wrong measurements, so returns
 * need all of them.
 */
export function packageMissingMeasurements(
  pallets: readonly Pick<ShipmentPallet, 'weightKg' | 'lengthCm' | 'widthCm' | 'heightCm'>[],
): number | null {
  const index = pallets.findIndex(
    (p) => !(p.weightKg > 0 && p.lengthCm > 0 && p.widthCm > 0 && p.heightCm > 0),
  );
  return index === -1 ? null : index + 1;
}

// A return from a customer Site to a Tidal location, via leatherback's
// CreateReturnShipmentViaCargonizer. `origin` holds the pickup details as
// they should appear on the label -- prefilled from the Site, but a one-off
// edit here never changes the Site.
export type ShipmentCreateReturnInput = {
  carrier: (typeof CARGONIZER_RETURN_CARRIERS)[number];
  originSiteId: string;
  origin: {
    name: string;
    contactName?: string | undefined;
    contactPhone?: string | undefined;
    address: ShipmentAddress;
  };
  destinationSiteId: string;
  pallets: Pick<ShipmentPallet, 'weightKg' | 'lengthCm' | 'widthCm' | 'heightCm'>[];
  reason?: string | undefined;
  // Required: Cargonizer's sender's reference ("Avsenders referanse"), the
  // project number X-Ledger rebills the freight to, or "Intern frakt"
  // (tidaltech/koi#8233).
  senderReference: string;
};

export function directionLabel(direction: ShipmentDirection): string {
  return direction === 'RETURN' ? 'Customer return' : 'Outbound';
}

// "From → To" names for a shipment, e.g. "Mowi Kråknes → Tidal Warehouse".
export function shipmentRoute(
  shipment: Pick<Shipment, 'direction' | 'origin' | 'destinationCustomer'>,
): {
  from: string;
  to: string;
} {
  return {
    from: shipment.origin?.name ?? 'Tidal',
    to: shipment.destinationCustomer,
  };
}

// A destination from Cargonizer's own address book ("kunder" in its UI) --
// lets the create-shipment form offer picking a known site/vendor instead
// of retyping its address every time.
export interface CargonizerCustomer {
  id: string;
  name: string;
  address: ShipmentAddress;
  contactPerson?: string | undefined;
  email?: string | undefined;
  phone?: string | undefined;
}

export function formatShipmentAddress(address: ShipmentAddress | undefined): string {
  if (!address) return '';
  return [address.line1, address.line2, `${address.postcode} ${address.city}`.trim()]
    .filter(Boolean)
    .join(', ');
}

// Instructions in Norwegian an operator can paste into an email to the
// customer, alongside the label PDF. Worded by the warehouse: Posten/Bring
// returns are dropped off, Nordvest returns are collected at the site, and
// both are returned to TidalX in Kristiansund.
export function returnInstructions(shipment: Shipment): string {
  const origin = shipment.origin;
  const several = shipment.pallets.length > 1;
  const labels = several ? 'de vedlagte etikettene' : 'den vedlagte etiketten';
  const nordvest = shipment.carrier === 'Nordvest Transport';
  const pickup = origin?.address ? formatShipmentAddress(origin.address) : (origin?.name ?? '');
  const lines = [
    `Retur til TidalX: sending ${shipment.cargonizerConsignmentNumber} (${shipment.carrier}).`,
    '',
    `1. Skriv ut og fest ${labels} på forsendelsen.`,
    nordvest
      ? `2. Transportør henter forsendelsen ved ${pickup}.`
      : '2. Pakken leveres snarest til din nærmeste Post i butikk.',
    `3. Forsendelsen returneres til ${RETURN_CONSIGNEE}.`,
  ];
  if (shipment.trackingUrl) lines.push('', `Spor sendingen: ${shipment.trackingUrl}`);
  return lines.join('\n');
}

// A dated, sourced observation about a shipment (tidaltech/koi#8086), e.g.
// Cargonizer handing it to the carrier or a Bring delivery event. Recorded
// by leatherback; never edited.
export type EvidenceSource = 'CARGONIZER' | 'BRING_TRACKING_API';

export interface ShipmentEvidence {
  id: string;
  source: EvidenceSource | 'SOURCE_UNSPECIFIED';
  confirmation: 'CONFIRMED' | 'INFERRED' | 'CONFIRMATION_UNSPECIFIED';
  // The source's own event code, e.g. "DELIVERED" or "TRANSFERRED_TO_CARRIER".
  kind: string;
  description?: string | undefined;
  eventTime?: ISODateTime | undefined;
  // The source gave no time for this event; eventTime is approximate.
  eventTimeApproximate: boolean;
  observedAt?: ISODateTime | undefined;
  location?: string | undefined;
  reference?: string | undefined;
  recordedBy?: string | undefined;
}

export interface TrackingRefreshResult {
  shipmentsChecked: number;
  evidenceRecorded: number;
  deliveriesRecorded: number;
  // False when leatherback has no Bring credentials: only Cargonizer's
  // hand-off to the carrier is recorded then.
  bringEnabled: boolean;
  errors: string[];
}

const EVIDENCE_KIND_LABELS: Record<string, string> = {
  TRANSFERRED_TO_CARRIER: 'Handed to carrier',
  PRE_NOTIFIED: 'Pre-notified',
  COLLECTED: 'Collected',
  HANDED_IN: 'Handed in',
  IN_TRANSIT: 'In transit',
  TERMINAL: 'At terminal',
  TRANSPORT_TO_RECIPIENT: 'Out for delivery',
  NOTIFICATION_SENT: 'Recipient notified',
  READY_FOR_PICKUP: 'Ready for pickup',
  ATTEMPTED_DELIVERY: 'Delivery attempted',
  DELIVERED: 'Delivered',
  DELIVERED_SENDER: 'Returned to sender',
  RETURN: 'Being returned',
  DEVIATION: 'Deviation',
  DELIVERY_CANCELLED: 'Delivery cancelled',
  DELIVERY_CHANGED: 'Delivery changed',
  CUSTOMS: 'Customs',
};

export function evidenceKindLabel(kind: string): string {
  return EVIDENCE_KIND_LABELS[kind] ?? kind.replace(/_/g, ' ').toLowerCase();
}

export function evidenceSourceLabel(source: ShipmentEvidence['source']): string {
  return source === 'BRING_TRACKING_API' ? 'Bring' : source === 'CARGONIZER' ? 'Cargonizer' : 'Unknown';
}
